import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Plus, FileText, Copy, Check, Pencil, Trash2 } from "lucide-react";
import { TEMPLATE_TAGS } from "@/lib/constants";
import { toast } from "sonner";

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // template being edited, or null for new
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const bodyRef = useRef(null);

  const insertTag = (tag) => {
    const el = bodyRef.current;
    if (!el) { setBody((b) => b + tag); return; }
    const s = el.selectionStart ?? body.length;
    const e = el.selectionEnd ?? body.length;
    const next = body.slice(0, s) + tag + body.slice(e);
    setBody(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = s + tag.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const load = () => api.get("/templates")
    .then(({ data }) => setTemplates(data))
    .catch((err) => toast.error(apiError(err)))
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setName(""); setBody(""); setDialogOpen(true); };
  const openEdit = (t) => { setEditing(t); setName(t.name); setBody(t.body); setDialogOpen(true); };

  const save = async () => {
    if (!name.trim()) { toast.error("Please give the template a name"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/templates/${editing.id}`, { name, body });
        toast.success("Template updated");
      } else {
        await api.post("/templates", { name, body });
        toast.success("Template created");
      }
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const copy = async (t) => {
    try {
      await navigator.clipboard.writeText(t.body || "");
      setCopiedId(t.id);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedId((c) => (c === t.id ? null : c)), 1800);
    } catch { toast.error("Couldn't copy — select and copy manually"); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/templates/${deleteTarget.id}`);
      toast.success("Template deleted");
      setDeleteTarget(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <PageHeader title="Templates" subtitle="Reusable description blocks to copy & send to editors" testid="templates-header">
        <Button onClick={openNew} data-testid="new-template-btn" className="gap-2">
          <Plus className="h-4 w-4" /> New template
        </Button>
      </PageHeader>

      <div className="p-6 md:p-8">
        {loading ? (
          <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : templates.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border py-16 text-center" data-testid="templates-empty">
            <FileText className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No templates yet. Create your first description template.</p>
            <Button className="mt-4 gap-2" onClick={openNew} data-testid="templates-empty-new-btn">
              <Plus className="h-4 w-4" /> New template
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="templates-list">
            {templates.map((t) => (
              <div key={t.id} className="flex flex-col rounded-xl border border-border bg-card p-5" data-testid={`template-card-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-heading text-lg font-bold tracking-tight" data-testid={`template-name-${t.id}`}>{t.name}</h3>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-8 gap-1.5" onClick={() => copy(t)} data-testid={`template-copy-${t.id}`}>
                      {copiedId === t.id ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                      {copiedId === t.id ? "Copied" : "Copy"}
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(t)} data-testid={`template-edit-${t.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => setDeleteTarget(t)} data-testid={`template-delete-${t.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="mt-3 max-h-64 overflow-y-auto whitespace-pre-wrap rounded-lg border border-border bg-secondary/30 p-3 text-sm text-foreground/90"
                  data-testid={`template-body-${t.id}`}>
                  {t.body || <span className="text-muted-foreground">No description yet.</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit template" : "New template"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Template name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="template-name-input"
                placeholder="e.g. Social Video" autoFocus />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Description</label>
              <div className="rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5 p-2.5" data-testid="dialog-tag-list">
                <div className="mb-1.5 text-[11px] font-medium text-muted-foreground">Click a tag to insert it at your cursor:</div>
                <div className="flex flex-wrap gap-1.5">
                  {TEMPLATE_TAGS.map((t) => (
                    <button key={t.tag} type="button" onClick={() => insertTag(t.tag)} title={`${t.label} · e.g. ${t.example}`}
                      data-testid={`insert-tag-${t.tag}`}
                      className="rounded-full border border-fuchsia-500/40 bg-card px-2.5 py-1 text-[11px] font-medium text-fuchsia-600 transition-colors hover:bg-fuchsia-500/15 dark:text-fuchsia-300">
                      <code>{t.tag}</code>
                    </button>
                  ))}
                </div>
              </div>
              <Textarea ref={bodyRef} value={body} onChange={(e) => setBody(e.target.value)} data-testid="template-body-input"
                rows={12} placeholder="Write the full description here. Line breaks and blank lines are preserved."
                className="resize-y whitespace-pre-wrap font-mono text-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="template-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="template-save-btn" className="gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {editing ? "Save changes" : "Create template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="template-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleteTarget?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>This template will be permanently removed. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="template-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="template-delete-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
